/**
 * disclosureCopy.js — the Wisconsin seller-disclosure copy the negotiation
 * workspace shows, verbatim from
 * docs/legal/states/WI/paperwork-copy-and-rules.v1.md §4.1
 * (`WI.disclosure.workspace.*`).
 *
 * Wisconsin law lets the seller furnish the report up to 10 days after
 * acceptance (Wis. Stat. § 709.02(1)). Completing it before the listing goes
 * live is a cutthe6.com product rule, so nothing here may say the law requires
 * it before acceptance, and the workspace step is labeled as a cutthe6.com
 * step, not a legal gate.
 */

export const DISCLOSURE_BASIS_STATUTORY = 'statutory';
export const DISCLOSURE_BASIS_VOLUNTARY = 'voluntary';

/** `{title}` when the workspace doesn't say which report the listing uses. */
export const DEFAULT_DISCLOSURE_TITLE = 'Real Estate Condition Report';

export const WORKSPACE_DISCLOSURE_COPY = {
  // WI.disclosure.workspace.title
  title: ({ title }) => `Seller disclosure: ${title}`,
  // WI.disclosure.workspace.info.statutory / .voluntary
  info: {
    [DISCLOSURE_BASIS_STATUTORY]:
      "Wisconsin law (Wis. Stat. § 709.02) requires the seller to give the buyer this report within 10 days after an offer is accepted. If the buyer doesn't receive it in that time, the buyer may cancel within 2 business days after the 10-day period ends. On cutthe6.com, sellers complete the report before the listing goes live.",
    [DISCLOSURE_BASIS_VOLUNTARY]:
      "The seller provided this report voluntarily. Wisconsin law doesn't require it for land that includes buildings. On cutthe6.com, land sellers complete it before the listing goes live.",
  },
  // WI.disclosure.workspace.gate.*
  gateTitle: 'Seller disclosure (cutthe6.com step)',
  gateCompleted: ({ title }) => `${title} completed.`,
  gatePending: 'Not completed yet. Accepting an offer is still available.',
  gateBadgePending: 'Pending',
  gateBadgeCompleted: 'Completed',
  // WI.disclosure.workspace.post_accept.title.statutory / .voluntary
  postAcceptTitle: {
    [DISCLOSURE_BASIS_STATUTORY]: ({ title }) =>
      `Seller disclosure: ${title} (due within 10 days after acceptance)`,
    [DISCLOSURE_BASIS_VOLUNTARY]: ({ title }) => `Seller disclosure: ${title}`,
  },
  // WI.disclosure.workspace.post_accept.seller
  postAcceptSeller:
    'The report is completed and signed on your listing, not in this workspace. Finish it before generating the signing envelope.',
  // WI.disclosure.workspace.listing_guide, split so the path can be emphasized.
  listingGuideLead: 'Complete the report on your listing:',
  listingGuidePath: 'My Listings, then Edit, then Seller Disclosure.',
};

/**
 * The listing's disclosure basis from the workspace (arch §8.4
 * `seller_disclosure_basis`); anything unknown is statutory.
 */
export function workspaceDisclosureBasis(workspace) {
  return workspace?.seller_disclosure_basis === DISCLOSURE_BASIS_VOLUNTARY
    ? DISCLOSURE_BASIS_VOLUNTARY
    : DISCLOSURE_BASIS_STATUTORY;
}

/**
 * `{title}` for the workspace copy: the server's title (which already carries
 * any " (Voluntary)" suffix, arch §8.4), else the condition report.
 */
export function workspaceDisclosureTitle(workspace) {
  const title = workspace?.seller_disclosure_title;
  return typeof title === 'string' && title.trim() ? title.trim() : DEFAULT_DISCLOSURE_TITLE;
}
